import React, { useEffect, useState } from 'react';
import { urlMap } from '../../utils';
import PomodoroSessionCard from '../dashboard/chapterPage/PomodoroSessionCard';

export default function PublicReflectionProgress({
  authorSlug,
  points = [],
  reflectionById = null,
  fetchReflection = null,
  onSelectionChange = null,
}) {
  const [selectedPoint, setSelectedPoint] = useState(null);
  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!reflection) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setReflection(null);
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [reflection]);

  useEffect(() => {
    onSelectionChange?.(Boolean(selectedPoint));
  }, [selectedPoint, onSelectionChange]);
  const chartTop = 34;
  const chartBottom = 190;
  const chartLeft = 54;
  const grouped = points.reduce((result, point) => {
    const key = new Date(point.created_at).toLocaleDateString();
    const rows = result.get(key) || [];
    rows.push(point);
    result.set(key, rows);
    return result;
  }, new Map());
  const dateGroups = Array.from(grouped.entries());
  const widths = dateGroups.map(([, rows]) => Math.max(56, rows.length * 28));
  const starts = widths.map((_, index) => chartLeft + widths.slice(0, index).reduce((sum, width) => sum + width, 0));
  const graphWidth = Math.max(500, chartLeft + widths.reduce((sum, width) => sum + width, 0) + 24);
  const positions = new Map();
  const chronologicalPoints = points.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const mobileLinePath = (() => {
    let pathIndex = 0;
    return chronologicalPoints.map((point, rowIndex) => {
      const difficulty = Math.max(1, Math.min(5, Number(point.difficulty_score ?? 3)));
      const x = ((difficulty - 1) * 20) + 10;
      const y = (rowIndex * 52) + 26;
      const cmd = pathIndex === 0 ? 'M' : 'L';
      pathIndex += 1;
      return `${cmd} ${x} ${y}`;
    }).filter(Boolean).join(' ');
  })();

  dateGroups.forEach(([, rows], dateIndex) => {
    rows.slice().sort((a, b) => new Date(a.created_at) - new Date(b.created_at)).forEach((point, timeIndex) => {
      const slotWidth = widths[dateIndex] / rows.length;
      const score = Math.max(1, Math.min(5, Number(point.difficulty_score ?? 3)));
      positions.set(point.id, {
        x: starts[dateIndex] + slotWidth * (timeIndex + 0.5),
        y: score != null ? chartBottom - ((score - 1) / 4) * (chartBottom - chartTop) : chartBottom,
      });
    });
  });

  const selectPoint = async (point) => {
    setSelectedPoint(point);
    setReflection(null);
    const localReflection = reflectionById?.[point.id];
    if (localReflection) {
      setReflection(localReflection);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      let data;
      if (fetchReflection) {
        data = await fetchReflection(point.id);
      } else {
        const response = await fetch(urlMap.publicAuthorReflection(authorSlug, point.id));
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        data = await response.json();
      }
      setReflection(data);
    } catch {
      setReflection(null);
    } finally {
      setLoading(false);
    }
  };

  const closeReflection = () => {
    setSelectedPoint(null);
    setReflection(null);
  };

  if (points.length === 0) {
    return <div className="public-feed__empty">No reflections recorded yet.</div>;
  }

  return (
    <div className={`public-progress${reflection ? ' public-progress--with-reflection' : ''}`}>
      <div className="public-progress__chart" aria-label="Reflection difficulty over time">
        <svg
          viewBox={`0 0 ${graphWidth} 244`}
          preserveAspectRatio="xMinYMin meet"
          style={{ width: `${graphWidth}px`, minWidth: '100%', height: '100%', flex: '0 0 auto' }}
          role="img"
        >
          <text x="14" y="20" className="public-progress__label public-progress__title">Difficulty</text>
          {[1, 2, 3, 4, 5].map((score) => {
            const y = chartBottom - ((score - 1) / 4) * (chartBottom - chartTop);
            return (
              <g key={score}>
                <line x1={chartLeft} y1={y} x2={graphWidth - 16} y2={y} className="public-progress__grid" />
                <text x={chartLeft - 16} y={y + 4} textAnchor="middle" className="public-progress__label">{score}</text>
              </g>
            );
          })}
          {dateGroups.map(([key, rows], index) => {
            const center = starts[index] + widths[index] / 2;
            return (
              <g key={key}>
                <line x1={starts[index]} y1={chartTop} x2={starts[index]} y2={chartBottom} className="public-progress__date-line" />
                <text x={center} y="218" textAnchor="middle" className="public-progress__label">
                  {new Date(rows[0].created_at).toLocaleDateString([], { day: 'numeric', month: 'short' })}
                </text>
              </g>
            );
          })}
          <polyline points={points.map((point) => {
            const position = positions.get(point.id);
            return `${position.x},${position.y}`;
          }).join(' ')} className="public-progress__line" />
          {points.map((point, index) => {
            const position = positions.get(point.id);
            const hasScore = point.difficulty_score != null;
            const label = `${new Date(point.created_at).toLocaleString()}, difficulty ${point.difficulty_score ?? 3}`;
            const isSelected = selectedPoint?.id === point.id;
            return (
              <g key={point.id} role="button" tabIndex="0" aria-label={label}
                 className={`public-progress__point${!hasScore ? ' public-progress__point--unrated' : ''}`}
                onClick={() => selectPoint(point)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') selectPoint(point);
                }}>
                <title>{`Reflection ${index + 1} · ${label}`}</title>
                <circle cx={position.x} cy={position.y} r={isSelected ? 8 : 6} className={isSelected ? 'is-selected' : ''} />
                <text x={position.x} y={position.y - 11} textAnchor="middle" className="public-progress__point-time">
                  {new Date(point.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      <div className="public-progress__mobile-chart" aria-label="Reflection difficulty by date">
        <div className="public-progress__mobile-chart-head" aria-hidden="true">
          <span>Session difficulty</span>
          <span className="public-progress__mobile-levels">
            {[1, 2, 3, 4, 5].map((level) => <b key={level}>{level}</b>)}
          </span>
        </div>
        <div className="public-progress__mobile-chart-body">
          <svg
            className="public-progress__mobile-line"
            viewBox={`0 0 100 ${chronologicalPoints.length * 52}`}
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path d={mobileLinePath} />
          </svg>
          <ol className="public-progress__timeline">
            {chronologicalPoints.map((point) => {
            const hasScore = point.difficulty_score != null;
            const difficulty = Math.max(1, Math.min(5, Number(point.difficulty_score ?? 3)));
            const isSelected = selectedPoint?.id === point.id;
            return (
              <li key={point.id}>
                <span className="public-progress__mobile-date">
                  <strong>{new Date(point.created_at).toLocaleDateString([], { day: 'numeric', month: 'short', year: '2-digit' })}</strong>
                  <small>{new Date(point.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                </span>
                <button
                  type="button"
                  className={`public-progress__mobile-plot${isSelected ? ' is-selected' : ''}`}
                  aria-label={`${new Date(point.created_at).toLocaleString()}, ${hasScore ? `difficulty ${difficulty} of 5` : 'no rating'}. Open reflection.`}
                  onClick={() => selectPoint(point)}
                >
                  <span className="public-progress__mobile-grid" aria-hidden="true">
                    {[1, 2, 3, 4, 5].map((level) => <i key={level} />)}
                  </span>
                  <span
                    className={`public-progress__timeline-dot${!hasScore ? ' public-progress__timeline-dot--unrated' : ''}`}
                    style={{ '--difficulty-position': hasScore ? difficulty - 1 : 2 }}
                  >
                    {hasScore ? difficulty : '–'}
                  </span>
                </button>
              </li>
            );
            })}
          </ol>
        </div>
      </div>

      {loading && <div className="public-feed__empty">Loading reflection...</div>}
      {reflection && (
        <ul className="pomodoro-session-list public-progress__reflection public-progress__reflection--desktop">
          <PomodoroSessionCard reflections={[reflection]} reflectionOnly />
        </ul>
      )}
      {reflection && (
        <div className="public-progress__dialog-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) closeReflection();
        }}>
          <section className="public-progress__dialog" role="dialog" aria-modal="true" aria-label="Reflection questions and answers">
            <header className="public-progress__dialog-header">
              <div>
                <strong>Reflection</strong>
                <span>{new Date(selectedPoint?.created_at).toLocaleString()}</span>
              </div>
              <button type="button" onClick={closeReflection} aria-label="Close reflection">×</button>
            </header>
            <ul className="pomodoro-session-list public-progress__reflection public-progress__reflection--dialog">
              <PomodoroSessionCard reflections={[reflection]} reflectionOnly />
            </ul>
          </section>
        </div>
      )}
    </div>
  );
}